import React, { useContext, useMemo } from "react";
import { Award, Check, Clock, ShieldCheck } from "lucide-react";

import { Badge } from "components/ui/badge";
import {
    TrustIcon,
    TrustPanel,
    TrustPanelItem,
    TrustPanelItemDescription,
    TrustPanelItemTitle,
    TrustPanelList,
    TrustPanelTitle,
} from "components/ui/trust-panel";
import { IntlContext } from "context";

import { formatOptional, subcategoryBase } from "../subcategoryIntl";

interface SubCategoryIntroProps {
    categoryKey: string;
    subcategoryKey: string;
}

const SubCategoryIntro: React.FC<SubCategoryIntroProps> = ({ categoryKey, subcategoryKey }) => {
    const intl = useContext(IntlContext);
    const base = subcategoryBase(categoryKey, subcategoryKey);
    const { formatMessage, hasMessage } = intl;

    const bodyDescription = formatMessage(`${base}.description`);

    const advantages = useMemo(() => {
        const keysRaw = formatOptional(hasMessage, formatMessage, `${base}.advantage_keys`);
        if (!keysRaw) return [];

        return keysRaw
            .split(",")
            .map((key) => key.trim())
            .filter(Boolean)
            .map((key) => {
                const id = `${base}.advantage.${key}`;
                const label = formatMessage(id);
                return label === id ? null : label;
            })
            .filter((label): label is string => label !== null);
    }, [base, formatMessage, hasMessage]);

    const advantagesTitle = formatMessage("subcategoryPage.advantages_title");
    const trustTitle = formatMessage("subcategoryPage.trust_title");

    const trustItems = useMemo(
        () => [
            {
                Icon: ShieldCheck,
                title: formatMessage("subcategoryPage.trust.profiles.title"),
                description: formatMessage("subcategoryPage.trust.profiles.description"),
            },
            {
                Icon: Award,
                title: formatMessage("subcategoryPage.trust.reviews.title"),
                description: formatMessage("subcategoryPage.trust.reviews.description"),
            },
            {
                Icon: Clock,
                title: formatMessage("subcategoryPage.trust.response.title"),
                description: formatMessage("subcategoryPage.trust.response.description"),
            },
        ],
        [formatMessage],
    );

    const hasAdvantages = advantages.length > 0;

    return (
        <section className="bg-background">
            <div className="mx-auto grid w-full max-w-[1400px] gap-8 px-4 py-10 sm:px-10 lg:grid-cols-3 lg:gap-10 lg:px-20 lg:py-12">
                <div className={hasAdvantages ? "space-y-6 lg:col-span-2" : "lg:col-span-2"}>
                    <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">{bodyDescription}</p>

                    {hasAdvantages && (
                        <div className="space-y-4">
                            <h2 className="text-lg font-bold tracking-tight text-foreground sm:text-xl">
                                {advantagesTitle}
                            </h2>
                            <ul className="grid gap-3 sm:grid-cols-2">
                                {advantages.map((label) => (
                                    <li key={label}>
                                        <Badge variant="advantage">
                                            <Check aria-hidden />
                                            {label}
                                        </Badge>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                <TrustPanel variant="soft">
                    <TrustPanelTitle>{trustTitle}</TrustPanelTitle>
                    <TrustPanelList>
                        {trustItems.map(({ Icon, title, description }) => (
                            <TrustPanelItem key={title}>
                                <TrustIcon palette="brand">
                                    <Icon aria-hidden />
                                </TrustIcon>
                                <div className="min-w-0 space-y-0.5 pt-0.5">
                                    <TrustPanelItemTitle>{title}</TrustPanelItemTitle>
                                    <TrustPanelItemDescription>{description}</TrustPanelItemDescription>
                                </div>
                            </TrustPanelItem>
                        ))}
                    </TrustPanelList>
                </TrustPanel>
            </div>
        </section>
    );
};

export default SubCategoryIntro;
